/**
 * ServiceLocator - Dependency Injection Container
 * Manages global services for easy testing and reusability
 * Services are registered once and retrieved anywhere in the game
 */

class ServiceLocator {
    constructor() {
        this.services = new Map();
        this.factories = new Map();
    }

    /**
     * Register a service instance
     * @param {string} name - Service identifier
     * @param {Object} instance - Service instance
     */
    register(name, instance) {
        if (this.services.has(name)) {
            console.warn(`ServiceLocator: Overwriting existing service '${name}'`);
        }
        this.services.set(name, instance);
    }

    /**
     * Register a factory function for lazy instantiation
     * @param {string} name - Service identifier
     * @param {Function} factory - Factory function that returns service instance
     */
    registerFactory(name, factory) {
        this.factories.set(name, factory);
    }

    /**
     * Get a service by name
     * @param {string} name - Service identifier
     * @returns {Object} Service instance
     */
    get(name) {
        if (this.services.has(name)) {
            return this.services.get(name);
        }

        // Try to create from factory
        if (this.factories.has(name)) {
            const instance = this.factories.get(name)();
            this.services.set(name, instance);
            return instance;
        }

        console.error(`ServiceLocator: Service '${name}' not found`);
        return null;
    }

    /**
     * Check if a service is registered
     * @param {string} name - Service identifier
     * @returns {boolean}
     */
    has(name) {
        return this.services.has(name) || this.factories.has(name);
    }

    /**
     * Remove a service
     * @param {string} name - Service identifier
     */
    unregister(name) {
        this.services.delete(name);
        this.factories.delete(name);
    }

    /**
     * Clear all services
     */
    clear() {
        this.services.clear();
        this.factories.clear();
    }
}

// Service name constants
export const Services = {
    EVENT_BUS: 'eventBus',
    SCORE_MANAGER: 'scoreManager',
    AUDIO_MANAGER: 'audioManager',
    LEVEL_LOADER: 'levelLoader',
    SAVE_MANAGER: 'saveManager',
    PARTICLE_MANAGER: 'particleManager'
};

// Singleton instance
const serviceLocator = new ServiceLocator();
export default serviceLocator;
